import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./SavingsCalculator.module.css";

// Gross water/sewage estimate; purchase, installation and maintenance are excluded.
const LITERS_PER_FLUSH = 2;
const PRICE_PER_CUBIC_METER = 12; // ₪ per m³ (water + sewage, business tariff estimate)

const TRAFFIC_LEVELS = [
  { id: "low", label: "משרד / עסק קטן", usesPerDay: 40, icon: "mdi:office-building-outline" },
  { id: "medium", label: "בניין משרדים", usesPerDay: 90, icon: "mdi:domain" },
  { id: "high", label: "מבנה ציבורי עמוס", usesPerDay: 150, icon: "mdi:stadium-outline" },
];

const useCountUp = (target, active, duration = 1200) => {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setValue(target);
      return;
    }
    const from = fromRef.current;
    startRef.current = null;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      fromRef.current = target;
      setValue(target);
      return;
    }

    const step = (ts) => {
      if (startRef.current === null) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      fromRef.current = target;
    };
  }, [target, active, duration]);

  return value;
};

const SavingsCalculator = ({ onCtaClick }) => {
  const [urinals, setUrinals] = useState(4);
  const [traffic, setTraffic] = useState(TRAFFIC_LEVELS[1]);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const { litersPerYear, shekelsPerYear } = useMemo(() => {
    const safeUrinals = Math.max(1, Math.min(urinals, 100));
    const liters = safeUrinals * traffic.usesPerDay * LITERS_PER_FLUSH * 365;
    const shekels = (liters / 1000) * PRICE_PER_CUBIC_METER;
    return {
      litersPerYear: liters,
      shekelsPerYear: Math.round(shekels),
    };
  }, [urinals, traffic]);

  const animatedLiters = useCountUp(litersPerYear, inView);
  const animatedShekels = useCountUp(shekelsPerYear, inView);

  const fmt = (n) => n.toLocaleString("he-IL");

  return (
    <section className={styles.section} ref={sectionRef} dir="rtl" aria-label="מחשבון חיסכון">
      <div className="container-fluid px-3 px-md-5">
        <div className={styles.header}>
          <span className={styles.eyebrow}>מחשבון חיסכון</span>
          <h2 className={styles.title}>כמה תחסכו עם משתנות ללא מים?</h2>
          <p className={styles.subtitle}>
            הזינו כמה משתנות יש במבנה ובחרו את רמת התנועה — ותקבלו הערכת חיסכון שנתית במים ובכסף.
          </p>
        </div>

        <div className={styles.card}>
          <div className={styles.controls}>
            <div className={styles.controlBlock}>
              <label htmlFor="calc-urinals" className={styles.controlLabel}>
                מספר משתנות במבנה
                <span className={styles.controlValue}>{urinals}</span>
              </label>
              <input
                id="calc-urinals"
                type="range"
                min="1"
                max="30"
                value={urinals}
                onChange={(e) => setUrinals(Number(e.target.value))}
                className={styles.slider}
                aria-valuetext={`${urinals} משתנות`}
              />
              <div className={styles.sliderScale} aria-hidden="true">
                <span>1</span>
                <span>30+</span>
              </div>
            </div>

            <div className={styles.controlBlock}>
              <span className={styles.controlLabel}>סוג המבנה / רמת תנועה</span>
              <div className={styles.trafficOptions} role="radiogroup" aria-label="רמת תנועה">
                {TRAFFIC_LEVELS.map((level) => (
                  <button
                    key={level.id}
                    type="button"
                    role="radio"
                    aria-checked={traffic.id === level.id}
                    className={`${styles.trafficBtn} ${
                      traffic.id === level.id ? styles.trafficBtnActive : ""
                    }`}
                    onClick={() => setTraffic(level)}
                  >
                    <span
                      className={`iconify ${styles.trafficIcon}`}
                      data-icon={level.icon}
                      aria-hidden="true"
                    ></span>
                    {level.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.results} aria-live="polite">
            <div className={styles.resultCard}>
              <span
                className={`iconify ${styles.resultIcon}`}
                data-icon="mdi:cash-multiple"
                aria-hidden="true"
              ></span>
              <div className={styles.resultValue}>₪{fmt(animatedShekels)}</div>
              <div className={styles.resultLabel}>חיסכון משוער במים ובביוב בשנה</div>
            </div>

            <div className={styles.resultCard}>
              <span
                className={`iconify ${styles.resultIcon}`}
                data-icon="mdi:water"
                aria-hidden="true"
              ></span>
              <div className={styles.resultValue}>{fmt(animatedLiters)}</div>
              <div className={styles.resultLabel}>ליטר מים בשנה</div>
            </div>

          </div>

          <div className={styles.footer}>
            <p className={styles.disclaimer}>
              הערכה בלבד, מבוססת על ~{LITERS_PER_FLUSH} ליטר להדחה, {traffic.usesPerDay} שימושים ביום למשתנה, 365 ימים בשנה ועלות מים וביוב של ~₪{PRICE_PER_CUBIC_METER} למ"ק. החיסכון בפועל תלוי בשימוש ובתעריף המים שלכם.
            </p>
            <p className={styles.disclaimer}>לא כולל עלות רכישה, התקנה, מחסניות ותחזוקה.</p>
            <button type="button" className={styles.cta} onClick={onCtaClick}>בקשת הצעת מחיר</button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SavingsCalculator;
